import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import { naira, pct, tone, stockHref, sectorLabel } from '../util';
const LIMIT = 10;
const COLS = [['symbol', 'Company'], ['current_price', 'Price'], ['change_percent', 'Today'], ['pct_change_7d', '7 days'], ['market_cap', 'Market value']];

/**
 * Phones: each company as one tappable row (symbol and name on the left, price and today's change on the right,
 * then 7 days · market value · sector). Plain list markup, so no table styles get in the way.
 */
export function stockCards(rows) {
  return m('ul.VouchaMk-cards', rows.map(s => m('li', {
    key: s.symbol
  }, m(Link, {
    href: stockHref(s.symbol)
  }, [m('span.VouchaMk-cardTop', [m('span.VouchaMk-cardName', [m('strong', s.symbol), m('small', s.name)]), m('span.VouchaMk-cardPrice', [m('strong', naira(s.current_price)), m('small', {
    className: tone(s.change_percent)
  }, pct(s.change_percent) + ' today')])]), m('span.VouchaMk-cardMore', [m('span', ['7 days ', m('b', {
    className: tone(s.pct_change_7d)
  }, pct(s.pct_change_7d))]), m('span', ['Value ', m('b', naira(s.market_cap, true))]), m('span', sectorLabel(s.sector))])]))));
}

/**
 * Every listed company: sortable, searchable, filterable by sector (attrs.sector, attrs.onClearSector). A table on
 * wide screens; on phones each row becomes a card (forum.less).
 */
export default class StockTable extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.sort = {
      key: 'market_cap',
      dir: 'desc'
    };
    this.search = '';
    this.all = false;
  }
  view() {
    const sector = this.attrs.sector;
    const q = this.search.trim().toLowerCase();
    const rows = this.attrs.stocks.filter(s => !sector || sectorLabel(s.sector) === sector).filter(s => !q || s.symbol.toLowerCase().includes(q) || String(s.name || '').toLowerCase().includes(q)).sort((a, b) => {
      const dir = this.sort.dir === 'asc' ? 1 : -1;
      const x = a[this.sort.key];
      const y = b[this.sort.key];
      if (typeof x === 'string' || typeof y === 'string') return String(x || '').localeCompare(String(y || '')) * dir;
      return ((x === null || x === undefined ? -Infinity : x) - (y === null || y === undefined ? -Infinity : y)) * dir;
    });
    const capped = !q && !sector && !this.all && rows.length > LIMIT;
    const shown = capped ? rows.slice(0, LIMIT) : rows;
    return m('section.VouchaMarkets-section#all-stocks', [m('h2', sector ? sector + ' companies' : capped ? 'Top ' + LIMIT + ' companies by market value' : 'All listed companies'), m('.VouchaMk-tools', [m('input.VouchaMarkets-search', {
      type: 'search',
      placeholder: 'Search by symbol or name',
      'aria-label': 'Search companies',
      value: this.search,
      oninput: e => this.search = e.target.value
    }),
    // phones: the column headings are hidden, so sorting is a menu
    m('select.VouchaMk-sortSelect', {
      'aria-label': 'Sort by',
      value: this.sort.key + ':' + this.sort.dir,
      onchange: e => {
        const [key, dir] = e.target.value.split(':');
        this.sort = {
          key,
          dir
        };
      }
    }, [['market_cap:desc', 'Largest first'], ['change_percent:desc', 'Up most today'], ['change_percent:asc', 'Down most today'], ['pct_change_7d:desc', 'Up most in 7 days'], ['pct_change_7d:asc', 'Down most in 7 days'], ['current_price:desc', 'Highest price'], ['symbol:asc', 'A to Z']].map(([v, label]) => m('option', {
      value: v
    }, label))), sector ? m('button.VouchaMk-chip', {
      type: 'button',
      onclick: () => this.attrs.onClearSector()
    }, [sector, m('span', {
      'aria-hidden': 'true'
    }, ' ×'), m('span.sr-only', ' (show all sectors)')]) : null]), stockCards(shown), m('table.VouchaMarkets-table.VouchaMk-stocks', [m('thead', m('tr', COLS.map(([key, label]) => {
      const on = this.sort.key === key;
      return m('th', {
        'aria-sort': on ? this.sort.dir === 'asc' ? 'ascending' : 'descending' : 'none'
      }, m('button.VouchaMk-sort', {
        type: 'button',
        onclick: () => this.sort = on ? {
          key,
          dir: this.sort.dir === 'asc' ? 'desc' : 'asc'
        } : {
          key,
          dir: key === 'symbol' ? 'asc' : 'desc'
        }
      }, [label, on ? m('span.VouchaMarkets-sortArrow', this.sort.dir === 'asc' ? ' ▲' : ' ▼') : null]));
    }).concat([m('th', 'Sector')]))), m('tbody', shown.map(s => m('tr', {
      key: s.symbol
    }, [m('td.VouchaMk-co', m(Link, {
      href: stockHref(s.symbol)
    }, [m('strong', s.symbol), m('span', s.name)])), m('td', {
      'data-label': 'Price'
    }, naira(s.current_price)), m('td', {
      'data-label': 'Today',
      className: tone(s.change_percent)
    }, pct(s.change_percent)), m('td', {
      'data-label': '7 days',
      className: tone(s.pct_change_7d)
    }, pct(s.pct_change_7d)), m('td', {
      'data-label': 'Market value'
    }, naira(s.market_cap, true)), m('td.VouchaMk-sector', {
      'data-label': 'Sector'
    }, sectorLabel(s.sector))])))]), q && !rows.length ? m('p.VouchaMarkets-note', 'No companies match "' + this.search.trim() + '".') : null, capped ? m('.VouchaMarkets-more', [m('button.Button', {
      type: 'button',
      onclick: () => this.all = true
    }, 'Show all ' + rows.length + ' companies'), m('span', 'or search above')]) : null, !q && !sector && this.all && rows.length > LIMIT ? m('.VouchaMarkets-more', m('button.Button', {
      type: 'button',
      onclick: () => this.all = false
    }, 'Show the top ' + LIMIT + ' only')) : null]);
  }
}
flarum.reg.add('voucha-markets', 'forum/components/StockTable', StockTable);